import axios, { AxiosRequestConfig } from 'axios';
import { baseURL } from '../config';
import ApiHelper from './ApiHelper';
import IResponse from '../interfaces/IResponse';

const BASE_URL = `${baseURL}api/notifications`;

export type NotificationTarget = 'showroom' | 'ufo';

export interface INotification {
	id: string;
	type: 'manual';
	targets: NotificationTarget[];
	title: string;
	message: string;
	displayAt: string;
	expiresAt: string | null;
	deletedAt: string | null;
	createdBy?: string;
	createdAt?: string;
	updatedAt?: string;
}

export interface INotificationPayload {
	targets: NotificationTarget[];
	title: string;
	message: string;
	displayAt?: string;
	expiresAt?: string | null;
	createdBy?: string;
}

interface IGetAllNotificationsResponse extends IResponse {
	notifications: INotification[];
}

interface INotificationMutationResponse extends IResponse {
	notification: INotification | null;
}

const getAllNotifications = async (): Promise<IGetAllNotificationsResponse> => {
	try {
		const authConfig: AxiosRequestConfig = {
			headers: ApiHelper.createHeader(),
		};

		const res = await axios.get(`${BASE_URL}/admin`, authConfig);
		return {
			ok: true,
			status: res.status,
			message: res.data?.message || 'Pobrano powiadomienia',
			notifications: res.data?.notifications || [],
		};
	} catch (error) {
		let message = 'Nie udało się pobrać powiadomień';
		let status = 400;
		if (axios.isAxiosError(error) && error.response) {
			message = error.response.data?.message || message;
			status = error.response.status;
		}

		return {
			ok: false,
			status,
			message,
			notifications: [],
		};
	}
};

const createNotification = async (payload: INotificationPayload): Promise<INotificationMutationResponse> => {
	try {
		const authConfig: AxiosRequestConfig = {
			headers: ApiHelper.createHeader(),
		};

		const res = await axios.post(BASE_URL, payload, authConfig);
		return {
			ok: true,
			status: res.status,
			message: res.data?.message || 'Dodano powiadomienie',
			notification: res.data?.notification || null,
		};
	} catch (error) {
		let message = 'Nie udało się dodać powiadomienia';
		let status = 400;
		if (axios.isAxiosError(error) && error.response) {
			message = error.response.data?.message || message;
			status = error.response.status;
		}

		return {
			ok: false,
			status,
			message,
			notification: null,
		};
	}
};

const updateNotification = async (
	notificationId: string,
	payload: Partial<INotificationPayload>
): Promise<INotificationMutationResponse> => {
	try {
		const authConfig: AxiosRequestConfig = {
			headers: ApiHelper.createHeader(),
		};

		const res = await axios.put(`${BASE_URL}/${notificationId}`, payload, authConfig);
		return {
			ok: true,
			status: res.status,
			message: res.data?.message || 'Zaktualizowano powiadomienie',
			notification: res.data?.notification || null,
		};
	} catch (error) {
		let message = 'Nie udało się zaktualizować powiadomienia';
		let status = 400;
		if (axios.isAxiosError(error) && error.response) {
			message = error.response.data?.message || message;
			status = error.response.status;
		}

		return {
			ok: false,
			status,
			message,
			notification: null,
		};
	}
};

const deleteNotification = async (notificationId: string): Promise<INotificationMutationResponse> => {
	try {
		const authConfig: AxiosRequestConfig = {
			headers: ApiHelper.createHeader(),
		};

		const res = await axios.delete(`${BASE_URL}/${notificationId}`, authConfig);
		return {
			ok: true,
			status: res.status,
			message: res.data?.message || 'Usunięto powiadomienie',
			notification: res.data?.notification || null,
		};
	} catch (error) {
		let message = 'Nie udało się usunąć powiadomienia';
		let status = 400;
		if (axios.isAxiosError(error) && error.response) {
			message = error.response.data?.message || message;
			status = error.response.status;
		}

		return {
			ok: false,
			status,
			message,
			notification: null,
		};
	}
};

const notificationsController = {
	getAllNotifications,
	createNotification,
	updateNotification,
	deleteNotification,
};

export default notificationsController;
